import React from "react";
import { useNavigate } from "react-router-dom";

import NavBar from "../components/common/nav_bar";
import MaintenanceBanner from "../components/common/maintenance_banner";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import Skeleton from "../components/common/skeleton";
import StaleBanner from "../components/paper/StaleBanner";
import StatusRibbon from "../components/paper/StatusRibbon";
import PaperStrategyCard, {
    readStrategyFields,
    classifyStrategy,
    buildNewIdSet,
    NEW_WINDOW_DAYS,
} from "../components/paper/PaperStrategyCard";
import PaperStrategyGroupCard from "../components/paper/PaperStrategyGroupCard";
import PaperStrategyListView from "../components/paper/PaperStrategyListView";
import StrategyFamilyFilter from "../components/paper/StrategyFamilyFilter";
import StrategyTradeTimeline, {
    normalizeTrade,
    normalizePosition,
} from "../components/paper/StrategyTradeTimeline";
import { groupStrategies } from "../data/strategyGrouping";
import {
    groupByFamily,
    familyPnl,
    familyBadge,
    familyKeyOfStrategy,
    FAMILY_META,
    FAMILY_ORDER,
} from "../data/strategyFamily";
import RetiredStrategyCard, {
    PARADIGM_ORDER as RETIRED_PARADIGM_ORDER,
    groupRetiredByParadigm,
    splitRetiredPnL,
} from "../components/paper/RetiredStrategyCard";
import AdoptedAnalysisPanel from "../components/paper/AdoptedAnalysisPanel";
import EquityCurveChart from "./research/EquityCurveChart";

import {
    fetchPaperSummary,
    fetchPaperPortfolioEquity,
    fetchPaperHealth,
    fetchPaperRetired,
    restorePaperStrategy,
    fetchPaperAdopted,
    unadoptPaperStrategy,
    fetchPaperStrategyTrades,
    fetchPaperStrategyPositions,
    fetchPaperFirstSeen,
} from "../services/paperApi";
import {
    getDescribedStrategyIds,
    getStrategyMeta,
    mergeRetiredWithMeta,
    mergeAdoptedWithMeta,
} from "../data/strategyMeta";
import { verdictLabelOf, VERDICT_RANK, daysInPaper } from "../data/verdict";
import { searchStrategies, normalizeQuery } from "../data/strategySearch";
import { requireLoginOrThrow } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";
import { settledPool } from "../utils/settledPool";
import { formatKst } from "../utils/time";

const POLL_INTERVAL_MS = 10_000;
const STALE_FAIL_THRESHOLD = 3;

// 운영기간 holdout 게이트(일). verdict 의 g5(holdout 14일)와 동일 — 운영기간 필터 기준 (DAS-146).
const HOLDOUT_DAYS = 14;

// 체결 타임라인 — paper-trader 에 전역 거래 피드가 없어, 거래가 있는 전략들의
// /trades 를 병합한다. 매 폴링(10s)마다 N건 재요청하면 과하므로 60s 로 throttle.
const TIMELINE_PER_STRATEGY = 50; // 전략당 최근 체결 수
const TIMELINE_MAX = 500; // 병합 후 보관 상한
const TIMELINE_REFRESH_MS = 60_000;
// 타임라인 요청 동시성 상한 — paper-trader 는 SSH 리버스 터널 1개 뒤에 있어,
// 거래 보유 전략 수(수십 개)만큼 /trades·/positions 를 한꺼번에 쏘면 터널이
// 포화돼 간헐 503(타임아웃)이 났다. 이 503 이 같은 시점 summary 폴링까지 물고
// 늘어져 /research 데이터 깜빡임을 유발했다 (DAS-157/158). 소수로 나눠 굴려
// 터널을 보호한다. (요청 총량은 그대로, 순간 부하만 낮춘다.)
const TIMELINE_CONCURRENCY = 4;

// 보기 설정 영속 키 (localStorage). 서브뷰/보기모드 모두 새로고침 후 유지된다.
const PREF_SUBVIEW = "research_subview";
const PREF_VIEW_MODE = "research_view_mode";
// 판단 대상(체결 여부) 포커스 — 미체결(신호 대기) 전략을 분리해 "실제 체결된 가설"
// 중심으로 보드를 거른다 (DAS-147). 기본값은 'traded'(체결된 것만).
const PREF_FILTER_TRADED = "research_filter_traded";
// 전략 카드 그룹 표시 — 'individual'(기본, 전략을 하나씩 개별 카드로) | 'family'(같은
// 패밀리 h<숫자>를 한 카드로 합산). 같은 접두어라도 h1320/h1320b/h1320v2 처럼 구조가
// 다른 별개 전략은 개별로 봐야 손익을 정확히 판단할 수 있어 기본값을 개별로 둔다.
const PREF_GROUP_MODE = "research_group_mode";

/** localStorage 에서 보기 설정을 읽되, 허용 목록 밖 값은 fallback. */
function readPref(key, fallback, allowed) {
    try {
        const v = window.localStorage.getItem(key);
        return allowed.includes(v) ? v : fallback;
    } catch {
        return fallback;
    }
}

/**
 * go-server 프록시가 터널 순단 시 캐시(last-good)를 대신 내려줄 때 붙이는
 * X-Paper-Stale 헤더를 axios 응답에서 읽는다 (DAS-158). true 면 200 이지만
 * 신선하지 않은(캐시) 데이터라는 뜻.
 */
function isPaperStale(res) {
    const v = res?.headers?.["x-paper-stale"];
    return v === "true" || v === true;
}

function writePref(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // localStorage 불가 환경(시크릿 등) — 영속 없이 동작.
    }
}

/**
 * 페이지 보기 상태(탭·서브뷰·검색·정렬·필터)와 스크롤 위치를 SPA 세션 동안
 * 보존하는 모듈 레벨 스냅샷. 상세 전략 페이지로 들어갔다가 뒤로가기로 돌아오면
 * 컴포넌트가 언마운트→재마운트되며 로컬 state 가 초기화되는데, 이 스냅샷으로
 * 직전에 설정해 둔 보기 상태와 스크롤을 그대로 복원한다. (전체 새로고침 시에는
 * 스냅샷이 사라져 초기값으로 시작 — 의도된 동작.)
 */
let overviewUiSnapshot = null;

// 스냅샷에 보존할 UI 상태 키 목록. 데이터(summary/equity 등)는 재조회하므로 제외.
const SNAPSHOT_KEYS = [
    "activeTab",
    "subView",
    "viewMode",
    "searchQuery",
    "sortKey",
    "filterStatus",
    "filterVerdict",
    "filterUptime",
    "filterTraded",
    "filterFamily",
    "groupMode",
    "equityRange",
];

/** 전략(그룹 집계 포함)이 실제 체결된 적이 있는지 — trades>0 (DAS-147). */
function isTradedStrategy(s) {
    return readStrategyFields(s).trades > 0;
}

/**
 * 판단 대상(체결 여부) 포커스 필터. 'traded' 는 체결≥1, 'untraded' 는 미체결,
 * 'all' 은 전부 통과. 그룹은 멤버 합산 trades 로 판단한다(aggregateStats).
 */
function applyTradedFilter(list, filterTraded) {
    if (filterTraded === "all" || !Array.isArray(list)) return list;
    const want = filterTraded === "traded";
    return list.filter((s) => isTradedStrategy(s) === want);
}

/** 표시용 목록에서 체결/미체결 개수를 센다 — 포커스 토글 라벨에 노출. */
function countTraded(list) {
    let traded = 0;
    let untraded = 0;
    for (const s of Array.isArray(list) ? list : []) {
        if (isTradedStrategy(s)) traded += 1;
        else untraded += 1;
    }
    return { traded, untraded };
}

/**
 * PaperOverviewPage — /research 메인. paper-trader 라이브 모니터링.
 *
 * 백테스트 분석 카탈로그(legacy)를 대체한다. DAS-120 에서 legacy 5 페이지
 * 폐기 후 이 페이지가 그 자리를 차지. nav_bar 의 Research 메뉴 라벨/아이콘
 * 은 그대로 유지된다 (사용자 멘탈 모델 보호).
 */
class PaperOverviewPageInner extends React.Component {
    constructor(props) {
        super(props);
        // UI 보기 상태 기본값. 상세 페이지 뒤로가기 복원 시 스냅샷이 이 값을 덮어쓴다.
        const ui = {
            activeTab: "active", // 'active' | 'adopted' | 'retired'
            // 운영 중 탭 내부 서브뷰 — 차트/체결을 분리해 스크롤 피로를 줄인다 (DAS-142).
            subView: readPref(PREF_SUBVIEW, "board", ["board", "chart", "feed"]),
            // 전략 목록 보기 모드 — 'card'(기존 카드) | 'list'(고밀도 행) (DAS-142).
            viewMode: readPref(PREF_VIEW_MODE, "card", ["card", "list"]),
            searchQuery: "", // 전략 자유 텍스트 검색어 (id/이름/요약/패밀리, DAS-142)
            sortKey: "category", // 'category' | 'pnl-asc' | 'pnl-desc' | 'trades-desc' | 'recent' | 'added-desc' | 'verdict'
            filterStatus: "all", // 'all' | 'active' | 'idle' | 'waiting'
            filterVerdict: "all", // 'all' | 'go' | 'continue' | 'nogo' | 'dormant' (판정 필터)
            filterUptime: "all", // 'all' | 'mature'(14일+) | 'young'(14일 미만) — holdout 게이트 (DAS-146)
            // 판단 대상(체결 여부) — 'traded'(체결≥1, 기본) | 'untraded'(미체결) | 'all'.
            // 미체결(신호 대기) 전략을 분리해 "실제 체결된 가설" 중심 판단을 돕는다 (DAS-147).
            filterTraded: readPref(PREF_FILTER_TRADED, "traded", ["traded", "untraded", "all"]),
            filterFamily: "all", // 'all' | FAMILY_META 키 (유형별 보기)
            // 카드 그룹 표시 — 'individual'(기본, 전략 개별) | 'family'(패밀리 합산 카드).
            groupMode: readPref(PREF_GROUP_MODE, "individual", ["individual", "family"]),
            equityRange: "all", // '7d' | '30d' | 'all'
        };
        // 뒤로가기 복원 — 상세 페이지 진입 시 저장된 스냅샷을 기본값 위에 덮어쓴다.
        // 1회성으로 소비(즉시 비움)해, nav 로 /research 에 새로 진입한 경우엔 복원하지
        // 않고 깨끗한 기본값으로 시작하도록 한다.
        const snapshot = overviewUiSnapshot;
        overviewUiSnapshot = null;
        if (snapshot) {
            for (const k of SNAPSHOT_KEYS) {
                if (snapshot[k] !== undefined) ui[k] = snapshot[k];
            }
        }
        this.state = {
            collapsed: getNavCollapsed(),
            loading: true,
            summary: null,
            equity: [],
            health: null,
            retired: [], // 백엔드(/api/paper/retired) 폐기 레코드 + meta 머지 결과
            adopted: [], // 백엔드(/api/paper/adopted) 채택 레코드 + meta 머지 결과
            firstSeen: [], // 백엔드(/api/paper/first-seen) 전략 최초 관측 시각 — 신규 배지용
            restoringId: null, // 복원 진행 중인 전략 id
            unadoptingId: null, // 채택 해제 진행 중인 전략 id
            failureCount: 0,
            stale: false,
            lastUpdated: null,
            ...ui,
            timelineTrades: [], // 전략별 /trades 병합·정렬(최신순) 결과
            timelineLoading: false,
        };
        // 복원 대기 스크롤 위치 — 데이터 로드(loading→false) 후 콘텐츠가 그려지면 적용.
        this.pendingScrollY = snapshot?.scrollY ?? null;
        this.shouldPoll = true;
        this.isPolling = false;
        this.lastTimelineFetch = 0;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        document.addEventListener("visibilitychange", this.onVisibilityChange);
        this.pollData();
    }

    componentDidUpdate(prevProps, prevState) {
        // 뒤로가기 복원 — 첫 데이터 로드가 끝나(loading true→false) 콘텐츠가 실제
        // 높이로 그려진 직후 저장해 둔 스크롤 위치로 되돌린다. 레이아웃 확정 시점을
        // 잡기 위해 두 번의 rAF 뒤에 적용한다.
        if (this.pendingScrollY != null && prevState.loading && !this.state.loading) {
            const y = this.pendingScrollY;
            this.pendingScrollY = null;
            requestAnimationFrame(() =>
                requestAnimationFrame(() => window.scrollTo(0, y)),
            );
        }
    }

    componentWillUnmount() {
        this.shouldPoll = false;
        clearTimeout(this.pollTimer);
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }

    /**
     * 현재 보기 상태(필터·정렬·탭·서브뷰)와 스크롤 위치를 모듈 스냅샷에 저장한다.
     * 상세 페이지로 이동하기 직전에만 호출해, 뒤로가기로 돌아올 때 복원의 소스로 쓴다.
     * (모든 언마운트가 아니라 상세 진입 시에만 저장 → 다른 경로 진입 시 오염 방지.)
     */
    saveUiSnapshot = () => {
        const snap = {};
        for (const k of SNAPSHOT_KEYS) snap[k] = this.state[k];
        snap.scrollY = window.scrollY || window.pageYOffset || 0;
        overviewUiSnapshot = snap;
    };

    onVisibilityChange = () => {
        this.shouldPoll = !document.hidden;
        if (this.shouldPoll && !this.isPolling) this.pollData();
    };

    pollData = () => {
        if (!this.shouldPoll) return;
        this.isPolling = true;

        Promise.allSettled([
            fetchPaperSummary(),
            fetchPaperPortfolioEquity(),
            fetchPaperHealth(),
            fetchPaperRetired(),
            fetchPaperAdopted(),
            fetchPaperFirstSeen(),
        ])
            .then(([summaryRes, equityRes, healthRes, retiredRes, adoptedRes, firstSeenRes]) => {
                // unmount / 백그라운드 진입 시 setState 보호.
                if (!this.shouldPoll) return;
                // last-good 유지 — 폴링 1회 실패로 화면을 통째로 비우지 않는다 (DAS-157).
                // 간헐 503(SSH 터널 너머 paper-trader 버스트 타임아웃)이 실패↔성공을
                // 번갈아 일으키면, 실패 시 null/[] 로 덮어쓰던 기존 코드가 "데이터 사라짐
                // → 10초 뒤 복구" 깜빡임을 만들었다. retired/adopted 와 동일하게 직전 값을
                // 유지하고, 지속 장애는 아래 failureCount/stale 배너가 별도로 알린다.
                const summary =
                    summaryRes.status === "fulfilled" ? summaryRes.value.data : this.state.summary;
                const equity =
                    equityRes.status === "fulfilled" ? equityRes.value.data : this.state.equity;
                const health =
                    healthRes.status === "fulfilled" ? healthRes.value.data : this.state.health;
                // 폐기 목록 실패는 stale 판정에서 제외 (active 모니터링이 핵심).
                // 실패 시 직전 목록 유지.
                const retired =
                    retiredRes.status === "fulfilled"
                        ? mergeRetiredWithMeta(retiredRes.value.data)
                        : this.state.retired;
                // 채택 목록도 동일 — 실패 시 직전 유지, stale 판정 제외.
                const adopted =
                    adoptedRes.status === "fulfilled"
                        ? mergeAdoptedWithMeta(adoptedRes.value.data)
                        : this.state.adopted;
                // first-seen 도 동일 — 실패 시 직전 유지, stale 판정 제외.
                const firstSeen =
                    firstSeenRes.status === "fulfilled" && Array.isArray(firstSeenRes.value.data)
                        ? firstSeenRes.value.data
                        : this.state.firstSeen;
                const anyFailed = [summaryRes, equityRes, healthRes].some(
                    (r) => r.status === "rejected",
                );
                const failureCount = anyFailed ? this.state.failureCount + 1 : 0;
                // go-server 가 터널 순단 시 캐시(last-good)를 200 으로 대신 내려주면
                // X-Paper-Stale: true 헤더가 붙는다 (DAS-158). 이때는 응답이 성공(200)
                // 이라 failureCount 는 오르지 않지만, 데이터는 신선하지 않으므로 "마지막
                // 갱신" 시각을 갱신하지 않는다 — 타임스탬프가 실제 신선도를 정직하게 반영.
                const anyStale = [summaryRes, equityRes, healthRes].some(
                    (r) => r.status === "fulfilled" && isPaperStale(r.value),
                );
                this.setState({
                    summary,
                    equity: Array.isArray(equity) ? equity : [],
                    health,
                    retired,
                    adopted,
                    firstSeen,
                    failureCount,
                    stale: failureCount >= STALE_FAIL_THRESHOLD,
                    lastUpdated:
                        anyFailed || anyStale ? this.state.lastUpdated : new Date(),
                    loading: false,
                });
                // 체결 타임라인은 summary 가 있을 때만, 60s throttle 로 갱신.
                if (summary) this.maybeFetchTimeline(summary);
            })
            .finally(() => {
                this.isPolling = false;
                if (this.shouldPoll)
                    this.pollTimer = setTimeout(this.pollData, POLL_INTERVAL_MS);
            });
    };

    /**
     * 60s throttle 게이트. summary 의 거래 보유 전략이 바뀌어도 다음 주기에 반영된다.
     */
    maybeFetchTimeline = (summary) => {
        if (Date.now() - this.lastTimelineFetch < TIMELINE_REFRESH_MS) return;
        this.fetchTimeline(summary);
    };

    /**
     * 타임라인 데이터 병합 — paper-trader 에 전역 거래 피드가 없어 클라이언트에서
     * 합친다 (read-only 프록시 원칙 유지).
     *   - trades>0 전략의 /trades  → 청산 완료(실현 손익)
     *   - open_count>0 전략의 /positions → 미청산(진입중, 평가손익)
     * 신호 대기(거래·포지션 0) 전략은 호출에서 제외해 요청 수를 줄인다.
     * 결과는 [진입중(진입 최신순) … , 청산(청산 최신순) …] 순으로 합쳐
     * timelineTrades 에 담는다. 컴포넌트가 open 플래그로 그룹을 나눈다.
     */
    fetchTimeline = (summary) => {
        const list = summary?.strategies ?? [];
        const tradeTargets = list.filter((s) => readStrategyFields(s).trades > 0);
        const posTargets = list.filter((s) => readStrategyFields(s).openCount > 0);
        this.lastTimelineFetch = Date.now();
        if (tradeTargets.length === 0 && posTargets.length === 0) {
            this.setState({ timelineTrades: [], timelineLoading: false });
            return;
        }
        this.setState({ timelineLoading: true });

        const tagOf = (s) => {
            const id = readStrategyFields(s).id;
            return {
                id,
                name: getStrategyMeta(id)?.name || id,
                family: familyKeyOfStrategy(s),
            };
        };

        // trades·positions 를 하나의 작업 큐로 합쳐 동시성 TIMELINE_CONCURRENCY 로
        // 제한한다. (기존엔 두 allSettled 가 각각 전량을 동시에 쏘아 터널을 포화시켰다.)
        // 결과는 앞쪽 tradeTargets 개 = 거래, 뒤쪽 = 포지션으로 분리해 기존 소비 코드
        // (tradeRes/posRes 각각의 forEach) 를 그대로 유지한다.
        const tasks = [
            ...tradeTargets.map((s) => ({ kind: "trade", s })),
            ...posTargets.map((s) => ({ kind: "pos", s })),
        ];
        settledPool(tasks, TIMELINE_CONCURRENCY, (t) =>
            t.kind === "trade"
                ? fetchPaperStrategyTrades(readStrategyFields(t.s).id, TIMELINE_PER_STRATEGY)
                : fetchPaperStrategyPositions(readStrategyFields(t.s).id),
        )
            .then((settled) => {
                if (!this.shouldPoll) return;
                const tradeRes = settled.slice(0, tradeTargets.length);
                const posRes = settled.slice(tradeTargets.length);

                // 청산 완료
                const closed = [];
                tradeRes.forEach((r, i) => {
                    if (r.status !== "fulfilled") return;
                    const { id, name, family } = tagOf(tradeTargets[i]);
                    const arr = Array.isArray(r.value.data) ? r.value.data : [];
                    for (const t of arr) {
                        const n = normalizeTrade(t);
                        if (!n.exitMs) continue; // 미청산/이상치 제외
                        closed.push({
                            ...n,
                            open: false,
                            strategyId: id,
                            strategyName: name,
                            family,
                        });
                    }
                });
                closed.sort((a, b) => b.exitMs - a.exitMs);
                const trimmedClosed = closed.slice(0, TIMELINE_MAX);

                // 미청산(진입중)
                const open = [];
                posRes.forEach((r, i) => {
                    if (r.status !== "fulfilled") return;
                    const { id, name, family } = tagOf(posTargets[i]);
                    const arr = Array.isArray(r.value.data) ? r.value.data : [];
                    for (const p of arr) {
                        const n = normalizePosition(p);
                        open.push({
                            ...n,
                            open: true,
                            strategyId: id,
                            strategyName: name,
                            family,
                        });
                    }
                });
                open.sort((a, b) => b.sortMs - a.sortMs);

                this.setState({
                    timelineTrades: [...open, ...trimmedClosed],
                    timelineLoading: false,
                });
            })
            .catch(() => {
                if (this.shouldPoll) this.setState({ timelineLoading: false });
            });
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    onSubViewChange = (subView) => {
        this.setState({ subView });
        writePref(PREF_SUBVIEW, subView);
    };

    onViewModeChange = (viewMode) => {
        this.setState({ viewMode });
        writePref(PREF_VIEW_MODE, viewMode);
    };

    onGroupModeChange = (groupMode) => {
        this.setState({ groupMode });
        writePref(PREF_GROUP_MODE, groupMode);
    };

    onFilterTradedChange = (filterTraded) => {
        this.setState({ filterTraded });
        writePref(PREF_FILTER_TRADED, filterTraded);
    };

    onStrategyClick = (id) => {
        if (this.props.navigate) {
            // 상세 진입 직전 보기 상태·스크롤을 저장 → 뒤로가기 시 복원.
            this.saveUiSnapshot();
            this.props.navigate(`/research/strategies/${encodeURIComponent(id)}`);
        }
    };

    /**
     * 폐기 전략 복원 — 백엔드 레코드를 삭제하고 즉시 목록을 재조회한다.
     * 복원되면 다음 폴링에서 paper-trader 가 여전히 운영 중인 경우 운영 탭에
     * 다시 나타난다. (DAS-126)
     */
    onRestore = (id) => {
        if (this.state.restoringId) return;
        this.setState({ restoringId: id });
        restorePaperStrategy(id)
            .then(() => fetchPaperRetired())
            .then((res) => {
                this.setState({ retired: mergeRetiredWithMeta(res.data) });
                // summary 도 즉시 갱신해 복원 전략이 운영 그리드에 곧바로 나타나도록
                // 재동기화 (retired 에서만 빠지고 active 엔 아직 없는 공백 구간 제거).
                clearTimeout(this.pollTimer);
                this.pollData();
            })
            .catch(() => {
                // 복원 실패 시 다음 폴링이 목록을 다시 맞춘다 — 조용히 무시.
            })
            .finally(() => this.setState({ restoringId: null }));
    };

    /**
     * 채택 해제 — 백엔드 채택 레코드를 삭제하고 즉시 목록을 재조회한다.
     * 매매에는 영향을 주지 않으므로(paper-trader 호출 없음) 전략은 운영 그리드에
     * 그대로 남고 ⭐ 배지만 사라진다. (DAS-126)
     */
    onUnadopt = (id) => {
        if (this.state.unadoptingId) return;
        this.setState({ unadoptingId: id });
        unadoptPaperStrategy(id)
            .then(() => fetchPaperAdopted())
            .then((res) => {
                this.setState({ adopted: mergeAdoptedWithMeta(res.data) });
            })
            .catch(() => {
                // 해제 실패 시 다음 폴링이 목록을 다시 맞춘다 — 조용히 무시.
            })
            .finally(() => this.setState({ unadoptingId: null }));
    };

    /**
     * 검색어 + filterStatus + filterVerdict + filterUptime 적용. 모두 비어있으면 전체 통과.
     * 판정 필터는 GO 후보만 모아 실전 고도화 판단을 하는 핵심 흐름 (DAS-142).
     * 운영기간 필터는 holdout(14일) 충족 전략만/미만만 분리해 검증 성숙도를 본다 (DAS-146).
     */
    filteredStrategies = (strategies) => {
        const { filterStatus, filterVerdict, filterUptime, filterTraded, searchQuery } =
            this.state;
        let list = searchStrategies(strategies, searchQuery);
        // 판단 대상(체결 여부) — "실제 체결된 가설" 중심 보기를 위해 가장 먼저 거른다 (DAS-147).
        list = applyTradedFilter(list, filterTraded);
        if (filterStatus !== "all") {
            list = list.filter((s) => {
                const f = readStrategyFields(s);
                return (
                    classifyStrategy({
                        openCount: f.openCount,
                        lastTradeTs: f.lastTradeTs,
                        trades: f.trades,
                    }) === filterStatus
                );
            });
        }
        if (filterVerdict !== "all") {
            list = list.filter(
                (s) =>
                    verdictLabelOf(s, this.familyVerdictMap, readStrategyFields(s)) ===
                    filterVerdict,
            );
        }
        if (filterUptime !== "all") {
            list = list.filter((s) => {
                const days = daysInPaper(readStrategyFields(s).firstTradeTs);
                return filterUptime === "mature"
                    ? days >= HOLDOUT_DAYS
                    : days < HOLDOUT_DAYS;
            });
        }
        return list;
    };

    /**
     * sortKey 기준으로 정렬한 새 배열 반환. 'category' 는 원본 순서 유지.
     */
    sortedStrategies = (strategies) => {
        const { sortKey } = this.state;
        const copy = [...strategies];
        switch (sortKey) {
            case "pnl-asc":
                return copy.sort(
                    (a, b) => readStrategyFields(a).pnl - readStrategyFields(b).pnl,
                );
            case "pnl-desc":
                return copy.sort(
                    (a, b) => readStrategyFields(b).pnl - readStrategyFields(a).pnl,
                );
            case "trades-desc":
                return copy.sort(
                    (a, b) => readStrategyFields(b).trades - readStrategyFields(a).trades,
                );
            case "recent":
                return copy.sort((a, b) => {
                    const ta = new Date(readStrategyFields(a).lastTradeTs || 0).getTime();
                    const tb = new Date(readStrategyFields(b).lastTradeTs || 0).getTime();
                    return tb - ta;
                });
            case "added-desc": {
                // 최근 추가순 — first-seen 시각 내림차순. 그룹은 멤버 중 가장 최근 first-seen 사용.
                const seen = this.firstSeenMap instanceof Map ? this.firstSeenMap : new Map();
                const addedTs = (s) => {
                    if (s?.isGroup) {
                        const members = s.members ?? [];
                        return members.reduce(
                            (mx, m) => Math.max(mx, seen.get(readStrategyFields(m).id) || 0),
                            seen.get(s.baseId) || 0,
                        );
                    }
                    return seen.get(readStrategyFields(s).id) || 0;
                };
                return copy.sort((a, b) => addedTs(b) - addedTs(a));
            }
            case "verdict": {
                // 판정순 — GO(실전 후보) → 검증중 → NO-GO(폐기 후보) → 데이터 부족.
                // 같은 판정 안에서는 PnL 높은순.
                const rankOf = (s) =>
                    VERDICT_RANK[
                        verdictLabelOf(s, this.familyVerdictMap, readStrategyFields(s))
                    ] ?? 9;
                return copy.sort((a, b) => {
                    const ra = rankOf(a);
                    const rb = rankOf(b);
                    if (ra !== rb) return ra - rb;
                    return readStrategyFields(b).pnl - readStrategyFields(a).pnl;
                });
            }
            case "category":
            default:
                return copy;
        }
    };

    /**
     * 진입중 카드를 정렬 결과 위로 끌어올림.
     * filter 가 'active' 또는 정렬이 의도적으로 진입중을 분산시키는 경우엔 호출 안 함.
     */
    activeFirst = (sorted) => {
        const active = [];
        const rest = [];
        for (const s of sorted) {
            const f = readStrategyFields(s);
            if (f.openCount > 0) active.push(s);
            else rest.push(s);
        }
        return [...active, ...rest];
    };

    /**
     * 그리드 표시 순서 결정 — 진입중 끌어올림(activeFirst)은 기본값(분류) 뷰에서만
     * 적용한다. 사용자가 PnL·거래·최근·추가·판정 등 "명시적 정렬"을 고르면 그 순서를
     * 그대로 존중한다. (그러지 않으면 상시 포지션을 보유하는 market-neutral 전략 등이
     * 정렬과 무관하게 늘 맨 앞에 고정되는 문제 — DAS-142 버그 리포트)
     */
    orderForGrid = (sorted) =>
        this.state.sortKey === "category" ? this.activeFirst(sorted) : sorted;

    /**
     * 그리드 카드 1개 렌더 — 코인 패밀리 그룹이면 PaperStrategyGroupCard(드릴다운),
     * 단일 전략이면 PaperStrategyCard.
     *
     * showFamily: 패밀리 섹션으로 묶인 뷰에서는 카드별 패밀리 칩이 중복이라 숨기고,
     *             평탄 정렬(PnL순 등) 뷰에서만 카드에 패밀리 칩을 노출한다.
     */
    renderCard = (s, adoptedIds, showFamily = false) => {
        const family = familyBadge(s);
        const verdictMap = this.familyVerdictMap;
        const newIds = this.newIds instanceof Set ? this.newIds : new Set();
        if (s?.isGroup) {
            return (
                <PaperStrategyGroupCard
                    key={`group:${s.baseId}`}
                    group={s}
                    onMemberClick={this.onStrategyClick}
                    adoptedIds={adoptedIds}
                    newIds={newIds}
                    family={family}
                    showFamily={showFamily}
                    verdict={verdictMap ? verdictMap.get(s.baseId) : null}
                />
            );
        }
        // 단일 카드는 VerdictBadge 가 strategy.verdict(백엔드 per-strategy 판정)를
        // 직접 읽는다 — 없으면 자체 stats 로 폴백.
        return (
            <PaperStrategyCard
                key={s.Strategy ?? s.strategy}
                strategy={s}
                onClick={this.onStrategyClick}
                adopted={adoptedIds.has(readStrategyFields(s).id)}
                isNew={newIds.has(readStrategyFields(s).id)}
                family={family}
                showFamily={showFamily}
            />
        );
    };

    /**
     * 전략 목록 렌더 — viewMode 에 따라 카드 그리드 또는 고밀도 리스트.
     * ordered 는 호출부에서 필터/정렬이 끝난 표시 순서 그대로의 목록.
     */
    renderCollection = (ordered, adoptedIds, showFamily = false) => {
        if (this.state.viewMode === "list") {
            return (
                <PaperStrategyListView
                    strategies={ordered}
                    onClick={this.onStrategyClick}
                    adoptedIds={adoptedIds}
                    newIds={this.newIds instanceof Set ? this.newIds : new Set()}
                    verdictMap={this.familyVerdictMap}
                    showFamily={showFamily}
                />
            );
        }
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 items-start">
                {ordered.map((s) => this.renderCard(s, adoptedIds, showFamily))}
            </div>
        );
    };

    /**
     * 패밀리 섹션/단일 유형 뷰 공통 헤더 — 글리프 + 한글 라벨 + 한 줄 설명 +
     * 개수 + 합산 PnL. list 는 해당 패밀리의 (표시) 전략 목록.
     */
    renderFamilyHeader = (key, list) => {
        const fam = FAMILY_META[key];
        const pnl = familyPnl(list);
        const pnlClass =
            pnl > 0 ? "text-gain" : pnl < 0 ? "text-loss" : "text-content-muted";
        return (
            <div className="flex items-start justify-between gap-3 mb-2.5 pb-2 border-b border-surface-border-light">
                <div className="flex items-start gap-2 min-w-0">
                    <span aria-hidden="true" className="text-base leading-none mt-0.5">
                        {fam.glyph}
                    </span>
                    <div className="min-w-0">
                        <div className="flex items-baseline gap-1.5">
                            <h3 className="text-sm font-semibold text-content">
                                {fam.label}
                            </h3>
                            <span className="text-xs text-content-muted">
                                {list.length}
                            </span>
                        </div>
                        <p className="text-xs text-content-secondary mt-0.5 leading-snug">
                            {fam.desc}
                        </p>
                    </div>
                </div>
                <span
                    className={`text-xs font-semibold tabular-nums shrink-0 ${pnlClass}`}
                    title="이 패밀리 합산 PnL"
                >
                    {Math.round(pnl).toLocaleString()} KRW
                </span>
            </div>
        );
    };

    renderStrategyGrid = (strategies, grouped, adoptedIds) => {
        const { sortKey, filterStatus, filterVerdict, filterUptime, filterFamily, searchQuery } =
            this.state;

        // 0) 검색어가 있으면 — 패밀리 섹션/유형 필터를 건너뛰고 전체에서 평탄 결과로
        //    집중해서 보여준다. 상태/판정 필터·정렬은 그대로 합성된다.
        if (normalizeQuery(searchQuery)) {
            const matched = this.filteredStrategies(strategies);
            const sorted = this.sortedStrategies(matched);
            const ordered = this.orderForGrid(sorted);
            return (
                <div>
                    <div className="text-xs text-content-secondary mb-3">
                        <span className="font-semibold text-content">
                            “{searchQuery.trim()}”
                        </span>{" "}
                        검색 결과{" "}
                        <span className="tabular-nums text-content">{ordered.length}</span>
                        건
                        <span className="text-content-muted">
                            {" "}
                            · 전체 {strategies.length} · 유형 구분 없이 표시
                        </span>
                    </div>
                    {ordered.length === 0 ? (
                        <div className="text-sm text-content-secondary py-6 text-center bg-surface-card border border-surface-border rounded-lg">
                            검색어와 일치하는 전략이 없습니다. 전략 ID(예: h318)·이름·코인으로
                            검색해 보세요.
                        </div>
                    ) : (
                        this.renderCollection(ordered, adoptedIds, true)
                    )}
                </div>
            );
        }

        // 1) 유형(패밀리) 필터가 특정 패밀리면 — 그 패밀리만, 헤더 1개 + 현재
        //    정렬/상태·판정필터를 적용한 평탄 목록으로 집중해서 보여준다.
        if (filterFamily !== "all") {
            const famList = grouped[filterFamily] || [];
            const base = this.filteredStrategies(famList);
            const sorted = this.sortedStrategies(base);
            const ordered = this.orderForGrid(sorted);
            return (
                <div>
                    {this.renderFamilyHeader(filterFamily, famList)}
                    {ordered.length === 0 ? (
                        <div className="text-sm text-content-secondary py-6 text-center bg-surface-card border border-surface-border rounded-lg">
                            이 유형에 조건을 만족하는 전략이 없습니다.
                        </div>
                    ) : (
                        this.renderCollection(ordered, adoptedIds)
                    )}
                </div>
            );
        }

        // 2) 전체 + 분류 정렬 + 필터 없음 — 의미 기반 패밀리 섹션 (모바일 가독성 핵심).
        //    각 섹션은 글리프 + 라벨 + 설명으로 "무슨 전략 묶음인지" 를 헤더만 봐도 안다.
        if (
            sortKey === "category" &&
            filterStatus === "all" &&
            filterVerdict === "all" &&
            filterUptime === "all"
        ) {
            const sections = FAMILY_ORDER.map((key) => {
                const raw = grouped[key];
                if (!raw || raw.length === 0) return null;
                // 판단 대상(체결 여부) 포커스를 패밀리 섹션 안에서도 적용한다 — 미체결만
                // 남는 패밀리는 섹션째 숨겨 "체결된 가설" 시야를 깨끗하게 유지 (DAS-147).
                const list = applyTradedFilter(raw, this.state.filterTraded);
                if (list.length === 0) return null;
                const sortedList = this.activeFirst(list);
                return (
                    <div key={key} className="mb-6">
                        {this.renderFamilyHeader(key, list)}
                        {this.renderCollection(sortedList, adoptedIds)}
                    </div>
                );
            }).filter(Boolean);
            if (sections.length === 0) {
                return (
                    <div className="text-sm text-content-secondary py-6 text-center bg-surface-card border border-surface-border rounded-lg">
                        {this.state.filterTraded === "traded"
                            ? "아직 체결이 진행된 전략이 없습니다. '전체' 또는 '미체결 대기'로 전환해 보세요."
                            : "조건에 맞는 전략이 없습니다."}
                    </div>
                );
            }
            return sections;
        }

        // 3) 전체 + 그 외 정렬/필터 — 단일 평탄 목록. 섹션 헤더가 없으므로 항목마다
        //    패밀리 칩을 노출(showFamily=true)한다.
        const filtered = this.filteredStrategies(strategies);
        const sorted = this.sortedStrategies(filtered);
        const ordered = this.orderForGrid(sorted);
        if (ordered.length === 0) {
            return (
                <div className="text-sm text-content-secondary py-6 text-center bg-surface-card border border-surface-border rounded-lg">
                    필터 조건에 맞는 전략이 없습니다.
                </div>
            );
        }
        return this.renderCollection(ordered, adoptedIds, true);
    };

    render() {
        const {
            collapsed,
            loading,
            summary,
            equity,
            health,
            stale,
            lastUpdated,
            activeTab,
            subView,
        } = this.state;

        const retired = this.state.retired;
        const adopted = this.state.adopted;
        // paper-trader 가 폐기 전략을 계속 스트리밍하더라도 운영 그리드에서는 제외.
        // 채택 전략은 매매를 유지하므로 운영 그리드에 그대로 남기되 ⭐ 배지만 표시한다.
        const retiredIds = new Set(retired.map((r) => r.id));
        const adoptedIds = new Set(adopted.map((a) => a.id));
        // rawStrategies — 코인별 개별 전략(retired 제외). AttentionBar 의 meta/휴면/손실
        // 탐지는 코인 단위로 정확해야 하므로 그룹핑 전 목록을 쓴다.
        const rawStrategies = (summary?.strategies ?? []).filter(
            (s) => !retiredIds.has(readStrategyFields(s).id),
        );
        // strategies — 표시용 목록. groupMode='family' 면 패밀리 단위 합산 카드로 묶고,
        // 'individual'(기본) 이면 전략을 하나씩 개별 카드로 둔다. 같은 접두어라도
        // h1320/h1320b/h1320v2 처럼 구조가 다른 별개 전략은 개별로 봐야 손익 판단이
        // 정확하므로 기본은 개별. 그리드/카테고리/정렬/필터/카운트가 모두 이 목록을 쓴다.
        const strategies =
            this.state.groupMode === "family" ? groupStrategies(rawStrategies) : rawStrategies;
        const total = summary?.total ?? {};
        const grouped = groupByFamily(strategies);
        // 판단 대상 포커스 토글 라벨용 — 체결/미체결 전략 수 (그룹 집계 기준, DAS-147).
        const tradedCounts = countTraded(strategies);
        // family_id → 백엔드 family verdict 맵. 그룹 카드는 합산 재계산 대신
        // 백엔드 판정(단일 소스)을 그대로 쓴다. 응답에 families 가 없으면(구버전)
        // 빈 맵 → VerdictBadge 가 그룹 합산 stats 로 폴백 계산.
        this.familyVerdictMap = new Map(
            (summary?.families ?? []).map((fam) => [fam.family_id, fam.verdict]),
        );
        // 신규(3일 내) 전략 id Set + 정렬용 first-seen 시각 맵 (renderCard/sortedStrategies 에서 사용).
        const firstSeen = this.state.firstSeen;
        this.newIds = buildNewIdSet(firstSeen);
        this.firstSeenMap = new Map(
            (Array.isArray(firstSeen) ? firstSeen : []).map((r) => [
                r.strategy_id,
                Date.parse(r.first_seen_at) || 0,
            ]),
        );

        const rangeEquity = filterEquityByRange(equity, this.state.equityRange);
        const equitySeries = buildEquitySeries(rangeEquity);
        // 진입중 평가손익(미실현) — 타임라인 open 항목(=positions UnrealizedKRW) 합.
        // 실현 기준 equity 스냅샷 끝점에 이 평가손익을 더해 "지금 살아있는" mark-to-market
        // 지점을 차트에 점선으로 투영한다 (DAS-147). 기준값(baseKrw)으로 index 100 환산.
        const openTimeline = this.state.timelineTrades.filter((t) => t.open);
        const openUnrealizedKrw = openTimeline.reduce((s, t) => s + t.pnl, 0);
        const baseEquityKrw =
            rangeEquity[0]?.equity_krw ?? rangeEquity[0]?.EquityKRW ?? 0;
        const lastEquityIndex = equitySeries.length
            ? equitySeries[equitySeries.length - 1].equity
            : 100;
        const equityLiveTip =
            baseEquityKrw && openTimeline.length > 0
                ? {
                      equityIndex:
                          lastEquityIndex + (openUnrealizedKrw / baseEquityKrw) * 100,
                      unrealizedKrw: openUnrealizedKrw,
                      openCount: openTimeline.length,
                  }
                : null;
        // 성과 탭 보강 — 누적수익률·MDD·기간 통계 + 손익 구성 분해용 (DAS-147).
        const equityStats = computeEquityStats(equitySeries);
        const equityRangeLabel =
            this.state.equityRange === "7d"
                ? "7일"
                : this.state.equityRange === "30d"
                  ? "30일"
                  : "전체";

        const groupedRetired = groupRetiredByParadigm(retired);
        const retiredCategories = RETIRED_PARADIGM_ORDER.concat(
            Object.keys(groupedRetired).filter((k) => !RETIRED_PARADIGM_ORDER.includes(k)),
        );

        const groupedAdopted = groupRetiredByParadigm(adopted);
        const adoptedCategories = RETIRED_PARADIGM_ORDER.concat(
            Object.keys(groupedAdopted).filter((k) => !RETIRED_PARADIGM_ORDER.includes(k)),
        );

        const showActive = activeTab === "active";
        const showAdopted = activeTab === "adopted";

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar
                    activeMenu="research"
                    collapsed={collapsed}
                    onCollapse={this.onCollapse}
                />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed
                            ? "sm:pl-[88px] lg:pl-[96px]"
                            : "sm:pl-[252px] lg:pl-[260px]"
                    } px-4 py-4 sm:pr-8 sm:py-6 lg:pr-10`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    <MaintenanceBanner />
                    <header className="mb-4">
                        <h1 className="text-2xl font-bold text-content">
                            Paper Trade 라이브 모니터링
                        </h1>
                        <p className="text-sm text-content-secondary mt-1">
                            BacktestResearch paper-trader (Mac 로컬) 의 {strategies.length || "—"} 활성
                            전략 실시간 상태. 5~10 초마다 자동 갱신.
                        </p>
                        <div className="text-xs text-content-muted mt-2">
                            마지막 갱신: {lastUpdated ? formatKst(lastUpdated) : "—"}
                        </div>
                        {health?.uptime_sec != null && (
                            <div className="text-xs text-content-muted mt-0.5">
                                paper-trader uptime: {fmtUptime(health.uptime_sec)}
                            </div>
                        )}
                    </header>

                    <TabBar
                        active={this.state.activeTab}
                        activeCount={strategies.length}
                        adoptedCount={adopted.length}
                        retiredCount={retired.length}
                        onChange={(tab) => this.setState({ activeTab: tab })}
                    />

                    {showActive && (<>
                    <StaleBanner
                        stale={stale}
                        lastUpdated={lastUpdated}
                        onRetry={this.pollData}
                    />

                    {loading && !summary ? (
                        <>
                            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                {[0, 1, 2, 3].map((i) => (
                                    <Skeleton key={i} variant="card" height="72px" />
                                ))}
                            </section>
                            <Skeleton variant="card" height="300px" className="mb-6" />
                            <Skeleton variant="card" height="180px" />
                        </>
                    ) : (
                        <>
                            <AttentionBar strategies={rawStrategies} newIds={this.newIds} />

                            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 sticky top-12 z-20 bg-surface -mx-4 px-4 py-2 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent">
                                <KpiCard
                                    label="총 거래"
                                    value={String(total.trades ?? 0)}
                                />
                                <KpiCard
                                    label="총 PnL (KRW)"
                                    value={Math.round(total.pnl_krw ?? 0).toLocaleString()}
                                    tone={
                                        (total.pnl_krw ?? 0) > 0
                                            ? "gain"
                                            : (total.pnl_krw ?? 0) < 0
                                              ? "loss"
                                              : "neutral"
                                    }
                                />
                                <KpiCard
                                    label="활성 포지션"
                                    value={String(total.open_count ?? 0)}
                                />
                                <KpiCard
                                    label="평균 WR"
                                    value={`${((total.win_rate ?? 0) * 100).toFixed(1)}%`}
                                />
                            </section>

                            <SubViewSwitcher
                                value={subView}
                                onChange={this.onSubViewChange}
                                feedCount={this.state.timelineTrades.length}
                            />

                            {subView === "chart" && (
                            <div className="space-y-4 mb-6">
                                {/* 성과 요약 스탯 — 차트 위 한눈 요약 */}
                                <EquityStatsRow
                                    stats={equityStats}
                                    unrealizedKrw={openUnrealizedKrw}
                                    openCount={openTimeline.length}
                                    rangeLabel={equityRangeLabel}
                                />

                                {/* Equity 곡선 차트 */}
                                <section className="bg-surface-card border border-surface-border rounded-lg p-4">
                                    <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
                                        <h2 className="text-base font-semibold text-content">
                                            포트폴리오 누적 Equity
                                        </h2>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <EquityRangeToggle
                                                value={this.state.equityRange}
                                                onChange={(v) =>
                                                    this.setState({ equityRange: v })
                                                }
                                            />
                                            <span className="text-xs text-content-muted">
                                                지수 100 = 시작
                                            </span>
                                        </div>
                                    </div>
                                    {equitySeries.length > 0 ? (
                                        <EquityCurveChart
                                            series={[
                                                {
                                                    label: "Portfolio",
                                                    data: equitySeries,
                                                },
                                            ]}
                                            liveTip={equityLiveTip}
                                            showDrawdown
                                            height={260}
                                        />
                                    ) : (
                                        <div className="text-sm text-content-secondary py-8 text-center">
                                            Equity 스냅샷 데이터 없음
                                        </div>
                                    )}
                                </section>

                                {/* 손익 구성 — 실현(청산완료) vs 평가(진입중) 분해 */}
                                <PnlBreakdownPanel
                                    realizedKrw={total.pnl_krw ?? 0}
                                    unrealizedKrw={openUnrealizedKrw}
                                    openCount={openTimeline.length}
                                    trades={total.trades ?? 0}
                                    winRate={total.win_rate ?? 0}
                                />
                            </div>
                            )}

                            {subView === "feed" && (
                            <section className="mb-6">
                                <StrategyTradeTimeline
                                    trades={this.state.timelineTrades}
                                    loading={this.state.timelineLoading}
                                    onRowClick={this.onStrategyClick}
                                />
                            </section>
                            )}

                            {subView === "board" && (
                            <section>
                                <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 mb-3">
                                    <h2 className="text-base font-semibold text-content">
                                        전략 ({this.filteredStrategies(strategies).length}/
                                        {strategies.length})
                                    </h2>
                                    <span className="inline-flex items-center gap-1 text-xs text-content-muted">
                                        <span
                                            aria-hidden="true"
                                            className="text-brand-dark bg-brand-subtle px-1 rounded font-semibold leading-none"
                                        >
                                            ✦신규
                                        </span>
                                        = 최근 {NEW_WINDOW_DAYS}일 내 추가된 전략
                                    </span>
                                </div>
                                <TradedFocusToggle
                                    value={this.state.filterTraded}
                                    tradedCount={tradedCounts.traded}
                                    untradedCount={tradedCounts.untraded}
                                    onChange={this.onFilterTradedChange}
                                />
                                <StrategySearchBox
                                    value={this.state.searchQuery}
                                    onChange={(v) => this.setState({ searchQuery: v })}
                                />
                                <StatusRibbon
                                    strategies={strategies}
                                    familyVerdictMap={this.familyVerdictMap}
                                    filterStatus={this.state.filterStatus}
                                    onFilterStatus={(v) => this.setState({ filterStatus: v })}
                                    filterVerdict={this.state.filterVerdict}
                                    onFilterVerdict={(v) => this.setState({ filterVerdict: v })}
                                />
                                <StrategyFamilyFilter
                                    grouped={grouped}
                                    value={this.state.filterFamily}
                                    onChange={(v) => this.setState({ filterFamily: v })}
                                />
                                <StrategyControls
                                    sortKey={this.state.sortKey}
                                    onSortChange={(v) => this.setState({ sortKey: v })}
                                    viewMode={this.state.viewMode}
                                    onViewModeChange={this.onViewModeChange}
                                    groupMode={this.state.groupMode}
                                    onGroupModeChange={this.onGroupModeChange}
                                    uptime={this.state.filterUptime}
                                    onUptimeChange={(v) => this.setState({ filterUptime: v })}
                                />
                                {this.renderStrategyGrid(strategies, grouped, adoptedIds)}
                            </section>
                            )}
                        </>
                    )}
                    </>)}

                    {showAdopted && (
                        <AdoptedSection
                            adopted={adopted}
                            summary={summary}
                            grouped={groupedAdopted}
                            categories={adoptedCategories}
                            onDetail={this.onStrategyClick}
                            onUnadopt={this.onUnadopt}
                            unadoptingId={this.state.unadoptingId}
                        />
                    )}

                    {!showActive && !showAdopted && (
                        <RetiredSection
                            retired={retired}
                            grouped={groupedRetired}
                            categories={retiredCategories}
                            onDetail={this.onStrategyClick}
                            onRestore={this.onRestore}
                            restoringId={this.state.restoringId}
                        />
                    )}

                    <ScrollToTopButton />
                </main>
            </div>
        );
    }
}

/**
 * SubViewSwitcher — 운영 중 탭 내부 서브뷰 세그먼트 (DAS-142).
 *
 * 차트·체결 피드를 전략 보드와 분리해 "전략 판단" 동선에서 스크롤로 지나칠
 * 콘텐츠를 없앤다. KPI/AttentionBar 는 모든 서브뷰에서 공통 유지된다.
 * 모바일: 3등분 풀폭 버튼(터치 타겟 44px+), 데스크톱: 컴팩트 세그먼트.
 */
function SubViewSwitcher({ value, onChange, feedCount = 0 }) {
    const opts = [
        { id: "board", glyph: "📋", label: "전략 보드" },
        { id: "chart", glyph: "📈", label: "성과 차트" },
        { id: "feed", glyph: "⚡", label: "체결 피드", count: feedCount },
    ];
    return (
        <div
            className="grid grid-cols-3 sm:inline-flex gap-1 bg-surface-card border border-surface-border rounded-lg p-1 mb-4"
            role="tablist"
            aria-label="운영 중 보기 영역"
        >
            {opts.map((o) => {
                const sel = value === o.id;
                return (
                    <button
                        key={o.id}
                        type="button"
                        role="tab"
                        aria-selected={sel}
                        onClick={() => onChange(o.id)}
                        className={`inline-flex items-center justify-center gap-1.5 text-xs sm:text-sm font-medium px-2 sm:px-4 py-2 rounded-md min-h-11 transition-colors ${
                            sel
                                ? "bg-brand-subtle text-brand-dark"
                                : "text-content-secondary hover:text-content hover:bg-surface-hover"
                        }`}
                    >
                        <span aria-hidden="true">{o.glyph}</span>
                        {o.label}
                        {o.count > 0 && (
                            <span
                                className={`tabular-nums ${
                                    sel ? "text-brand-dark" : "text-content-muted"
                                }`}
                            >
                                {o.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function TabBar({ active, activeCount, adoptedCount, retiredCount, onChange }) {
    const Btn = ({ id, label, count }) => {
        const sel = active === id;
        return (
            <button
                type="button"
                onClick={() => onChange(id)}
                role="tab"
                aria-selected={sel}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors min-h-11 ${
                    sel
                        ? "border-brand text-content"
                        : "border-transparent text-content-secondary hover:text-content"
                }`}
            >
                {label}
                <span
                    className={`ml-1.5 text-xs ${
                        sel ? "text-brand" : "text-content-muted"
                    }`}
                >
                    {count}
                </span>
            </button>
        );
    };
    return (
        <div
            className="flex items-center gap-1 border-b border-surface-border mb-4 -mx-4 px-4 sm:mx-0 sm:px-0"
            role="tablist"
            aria-label="전략 운영 상태"
        >
            <Btn id="active" label="운영 중" count={activeCount} />
            <Btn id="adopted" label="채택됨" count={adoptedCount} />
            <Btn id="retired" label="폐기됨" count={retiredCount} />
        </div>
    );
}

function AdoptedSection({ adopted, summary, grouped, categories, onDetail, onUnadopt, unadoptingId }) {
    if (adopted.length === 0) {
        return (
            <section className="bg-surface-card border border-surface-border rounded-lg p-6 text-center text-sm text-content-secondary">
                채택된 전략이 없습니다. 전략 디테일에서 "채택" 버튼으로 우수 전략을 북마크하세요.
            </section>
        );
    }
    return (
        <section>
            <AdoptedAnalysisPanel adopted={adopted} summary={summary} />
            <div className="bg-surface-card border border-surface-border rounded-lg p-3 mb-4 flex items-center gap-2">
                <span aria-hidden="true" className="text-warning">⭐</span>
                <div>
                    <div className="text-base font-semibold text-content">
                        {adopted.length} 건 채택
                    </div>
                    <div className="text-xs text-content-muted mt-0.5">
                        우수 전략 북마크 — 매매는 계속 운영 중이며 운영 그리드에도 ⭐ 로 표시된다.
                    </div>
                </div>
            </div>
            <p className="text-xs text-content-secondary mb-4">
                추후 아이디어 도출을 위해 수집한 전략. 채택은 매매를 중지하지 않는다.
            </p>
            {categories.map((paradigm) => {
                const list = grouped[paradigm];
                if (!list || list.length === 0) return null;
                return (
                    <div key={paradigm} className="mb-5">
                        <div className="flex items-center mb-2">
                            <span
                                aria-hidden="true"
                                className="inline-block w-1 h-3 bg-warning rounded-sm mr-2"
                            />
                            <div className="text-xs text-content-muted uppercase tracking-wide">
                                {paradigm} ({list.length})
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {list.map((s) => (
                                <AdoptedStrategyCard
                                    key={s.id}
                                    strategy={s}
                                    onClick={onDetail}
                                    onUnadopt={onUnadopt}
                                    unadopting={unadoptingId === s.id}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </section>
    );
}

/**
 * AdoptedStrategyCard — 채택된 전략 1건 카드. RetiredStrategyCard 의 채택 버전.
 * 채택은 손실 강조 대신 ⭐ 우수 표시 + "채택 해제" 버튼을 노출한다.
 */
function AdoptedStrategyCard({ strategy, onClick, onUnadopt, unadopting }) {
    const handle = () => onClick && onClick(strategy.id);
    return (
        <div className="relative bg-surface-card rounded-lg transition-colors border border-warning/40 border-l-2 border-l-warning">
            <button
                type="button"
                onClick={handle}
                className="text-left w-full block p-3 rounded-lg hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-brand"
                aria-label={`${strategy.id} 상세 보기 (채택됨)`}
            >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                        <span aria-hidden="true" className="text-warning shrink-0">⭐</span>
                        <span className="text-sm font-semibold text-content">{strategy.id}</span>
                        {strategy.paradigm && (
                            <span className="text-xs text-content-secondary bg-surface-elevated px-1.5 py-0.5 rounded shrink-0">
                                {strategy.paradigm}
                            </span>
                        )}
                    </div>
                    {strategy.adoptedAt && (
                        <div className="text-xs text-content-muted leading-tight shrink-0">
                            {strategy.adoptedAt}
                        </div>
                    )}
                </div>
                {strategy.name && (
                    <div className="text-sm text-content-secondary mb-1.5">{strategy.name}</div>
                )}
                {strategy.summary && (
                    <p className="text-xs text-content-secondary leading-relaxed mb-2 line-clamp-3">
                        {strategy.summary}
                    </p>
                )}
                {strategy.adoptedReason && (
                    <div className="text-xs text-content-secondary mt-2">
                        <span className="text-content-muted">채택 사유: </span>
                        {strategy.adoptedReason}
                    </div>
                )}
            </button>
            {onUnadopt && (
                <div className="flex justify-end px-3 pb-2 -mt-1">
                    <button
                        type="button"
                        onClick={() => !unadopting && onUnadopt(strategy.id)}
                        disabled={unadopting}
                        aria-label={`${strategy.id} 채택 해제`}
                        className="text-xs text-content-secondary hover:text-content border border-surface-border rounded px-2 py-1 min-h-11 hover:bg-surface-hover disabled:opacity-50 transition-colors"
                    >
                        {unadopting ? "해제 중…" : "채택 해제"}
                    </button>
                </div>
            )}
        </div>
    );
}

function RetiredSection({ retired, grouped, categories, onDetail, onRestore, restoringId }) {
    if (retired.length === 0) {
        return (
            <section className="bg-surface-card border border-surface-border rounded-lg p-6 text-center text-sm text-content-secondary">
                현재 폐기 처리된 전략이 없습니다.
            </section>
        );
    }
    const { lossSum, lossCount, integrationCount } = splitRetiredPnL(retired);
    return (
        <section>
            <div className="bg-surface-card border border-surface-border rounded-lg p-3 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <div className="text-xs text-content-muted">폐기 전략</div>
                    <div className="text-base font-semibold text-content mt-0.5">
                        {retired.length} 건
                        <span className="ml-1.5 text-xs text-content-muted font-normal">
                            단독 {lossCount} / 통합 {integrationCount}
                        </span>
                    </div>
                </div>
                <div>
                    <div className="text-xs text-content-muted">단독 paper 손실</div>
                    <div
                        className={`text-base font-semibold tabular-nums mt-0.5 ${
                            lossSum < 0 ? "text-loss" : lossSum > 0 ? "text-gain" : "text-content"
                        }`}
                        title={`${lossCount}건 axis 의 확정 손익 합계`}
                    >
                        {lossSum.toLocaleString()} KRW
                    </div>
                </div>
                <div>
                    <div className="text-xs text-content-muted">통합 정리</div>
                    <div className="text-base font-semibold text-content mt-0.5">
                        {integrationCount} 건
                        <span className="ml-1.5 text-xs text-content-muted font-normal">
                            trigger 중복
                        </span>
                    </div>
                </div>
            </div>
            <p className="text-xs text-content-secondary mb-4">
                paper trading 결과 또는 통합 정리로 운영 중단된 전략. 사유와 최종 누적 stats 를 참조용으로 보관한다.
            </p>
            {categories.map((paradigm) => {
                const list = grouped[paradigm];
                if (!list || list.length === 0) return null;
                return (
                    <div key={paradigm} className="mb-5">
                        <div className="flex items-center mb-2">
                            <span
                                aria-hidden="true"
                                className="inline-block w-1 h-3 bg-loss rounded-sm mr-2"
                            />
                            <div className="text-xs text-content-muted uppercase tracking-wide">
                                {paradigm} ({list.length})
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {list.map((s) => (
                                <RetiredStrategyCard
                                    key={s.id}
                                    strategy={s}
                                    onClick={onDetail}
                                    onRestore={onRestore}
                                    restoring={restoringId === s.id}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}
        </section>
    );
}

/**
 * 휴면 경고 임계치 — classifyStrategy('idle') 는 24h+ 이나 AttentionBar 는
 * 더 엄격하게 48h+ 만 노출 (관리자 매일 점검 흐름 가정).
 */
const ATTENTION_IDLE_HOURS = 48;
const ATTENTION_LOSS_KRW = -100_000;
const ATTENTION_LOSS_TOP_N = 3;

/**
 * AttentionBar — KPI 카드 아래에 "지금 봐야 할 N가지" 인라인 요약.
 *
 * 자동 탐지:
 *   1) 진입중(포지션 보유) 전략 수
 *   2) 손실 경고 — PnL 하위 3개 (임계치 -100K KRW 이하)
 *   3) 휴면(48h+ idle) 전략 수
 *   4) meta 미등록 — paper-trader summary 응답 ID 중 strategyMeta.js
 *      에 등록되지 않은 ID
 *
 * 표시할 항목이 하나도 없으면 컴포넌트 자체를 숨겨 노이즈를 줄인다.
 */
function AttentionBar({ strategies, newIds = null }) {
    if (!Array.isArray(strategies) || strategies.length === 0) return null;

    const newSet = newIds instanceof Set ? newIds : new Set();
    const enriched = strategies.map((s) => ({ s, f: readStrategyFields(s) }));
    const newcomers = enriched.filter((x) => newSet.has(x.f.id));
    const active = enriched.filter((x) => x.f.openCount > 0);
    const now = Date.now();
    const idle = enriched.filter((x) => {
        if (!x.f.lastTradeTs) return false;
        const hours = (now - new Date(x.f.lastTradeTs).getTime()) / 3600 / 1000;
        return hours >= ATTENTION_IDLE_HOURS && x.f.openCount === 0;
    });
    const losses = enriched
        .filter((x) => x.f.pnl <= ATTENTION_LOSS_KRW)
        .sort((a, b) => a.f.pnl - b.f.pnl)
        .slice(0, ATTENTION_LOSS_TOP_N);

    const metaIds = new Set(getDescribedStrategyIds());
    const missingMeta = enriched
        .map((x) => x.f.id)
        .filter((id) => id && !metaIds.has(id));

    const items = [];
    if (newcomers.length > 0) {
        items.push({
            key: "new",
            tone: "brand",
            dot: false,
            glyph: "✦",
            text: `신규 ${newcomers.length}개`,
            title: `최근 ${NEW_WINDOW_DAYS}일 내 추가: ${newcomers
                .map((x) => x.f.id)
                .join(", ")}`,
        });
    }
    if (active.length > 0) {
        items.push({
            key: "active",
            tone: "brand",
            dot: true,
            text: `진입중 ${active.length}개`,
            title: `포지션 보유 전략: ${active.map((x) => x.f.id).join(", ")}`,
        });
    }
    if (losses.length > 0) {
        const lossSummary = losses
            .map((x) => `${x.f.id} ${Math.round(x.f.pnl / 1000)}K`)
            .join(", ");
        items.push({
            key: "loss",
            tone: "loss",
            dot: false,
            text: `손실 경고: ${lossSummary}`,
            title: `PnL 하위 ${losses.length}개 — 폐기 후보 검토 권장`,
        });
    }
    if (idle.length > 0) {
        items.push({
            key: "idle",
            tone: "warning",
            dot: false,
            text: `휴면 ${idle.length}개 (${ATTENTION_IDLE_HOURS}h+)`,
            title: `${idle.map((x) => x.f.id).join(", ")}`,
        });
    }
    if (missingMeta.length > 0) {
        items.push({
            key: "missing",
            tone: "muted",
            dot: false,
            text: `meta 미등록 ${missingMeta.length}개: ${missingMeta.join(", ")}`,
            title: "strategyMeta.js 에 메타 추가 필요",
        });
    }

    if (items.length === 0) return null;

    const toneClass = {
        brand: "bg-brand-subtle text-brand-dark",
        loss: "bg-loss-subtle text-loss",
        warning: "bg-warning-subtle text-warning",
        muted: "bg-surface-elevated text-content-secondary border border-surface-border",
    };
    const dotClass = {
        brand: "bg-brand animate-pulse",
        loss: "bg-loss",
        warning: "bg-warning",
        muted: "bg-content-muted",
    };

    return (
        <div
            className="bg-surface-card border border-surface-border rounded-lg p-3 mb-4 flex flex-wrap gap-2"
            role="region"
            aria-label="주목할 전략 요약"
        >
            {items.map((it) => (
                <span
                    key={it.key}
                    title={it.title}
                    className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded ${
                        toneClass[it.tone] ?? toneClass.muted
                    }`}
                >
                    {it.dot && (
                        <span
                            aria-hidden="true"
                            className={`inline-block h-1.5 w-1.5 rounded-full ${
                                dotClass[it.tone] ?? dotClass.muted
                            }`}
                        />
                    )}
                    {it.glyph && <span aria-hidden="true">{it.glyph}</span>}
                    {it.text}
                </span>
            ))}
        </div>
    );
}

/**
 * TradedFocusToggle — "판단 대상" 1차 포커스 (DAS-147).
 *
 * 보드의 가장 위에 놓여, 미체결(신호 대기) 전략을 분리해 "실제 체결이 진행된
 * 가설" 중심으로 빠르게 판단하게 한다. 기본값 traded(체결됨)로, 한 번도 진입하지
 * 않은 전략이 그리드에서 빠진다. 각 버튼은 살아있는 개수를 함께 보여준다.
 */
function TradedFocusToggle({ value, tradedCount, untradedCount, onChange }) {
    const opts = [
        {
            id: "traded",
            glyph: "✔",
            label: "체결 진행됨",
            count: tradedCount,
            hint: "체결 1건 이상 — 성과로 판단 가능",
        },
        {
            id: "untraded",
            glyph: "○",
            label: "미체결 대기",
            count: untradedCount,
            hint: "한 번도 진입 안 함 — 신호 대기 중",
        },
        {
            id: "all",
            glyph: "≡",
            label: "전체",
            count: tradedCount + untradedCount,
            hint: "체결·미체결 모두 보기",
        },
    ];
    return (
        <div className="bg-surface-card border border-surface-border rounded-lg p-3 mb-3">
            <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
                <h3 className="text-sm font-medium text-content flex items-center gap-2">
                    판단 대상
                    <span className="text-xs text-content-muted font-normal">
                        실제 체결된 가설 중심으로 보기
                    </span>
                </h3>
                <span className="text-xs text-content-muted tabular-nums">
                    체결 {tradedCount} · 대기 {untradedCount}
                </span>
            </div>
            <div
                className="grid grid-cols-3 gap-2"
                role="group"
                aria-label="판단 대상 (체결 여부)"
            >
                {opts.map((o) => {
                    const sel = value === o.id;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => onChange(o.id)}
                            aria-pressed={sel}
                            title={o.hint}
                            className={`flex flex-col items-start gap-0.5 rounded-md border px-2.5 py-2 min-h-11 text-left transition-colors ${
                                sel
                                    ? "border-brand/50 bg-brand-subtle"
                                    : "border-surface-border hover:bg-surface-hover"
                            }`}
                        >
                            <span className="flex items-center gap-1.5">
                                <span
                                    aria-hidden="true"
                                    className={sel ? "text-brand-dark" : "text-content-muted"}
                                >
                                    {o.glyph}
                                </span>
                                <span
                                    className={`text-xs font-semibold ${
                                        sel ? "text-brand-dark" : "text-content"
                                    }`}
                                >
                                    {o.label}
                                </span>
                            </span>
                            <span
                                className={`text-sm font-bold tabular-nums ${
                                    sel ? "text-brand-dark" : "text-content"
                                }`}
                            >
                                {o.count}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * StrategySearchBox — 전략 자유 텍스트 검색 입력 (DAS-142).
 *
 * 전략 ID(h318)·한글 이름·요약·코인·패밀리로 즉시 필터. 검색어가 있으면
 * 운영 그리드가 패밀리 섹션을 접고 전체에서 평탄 결과로 바뀐다. 입력 좌측
 * 돋보기(장식 aria-hidden) + 입력 시 우측 지우기 버튼(aria-label).
 */
function StrategySearchBox({ value, onChange }) {
    return (
        <div className="relative mb-3">
            <span
                aria-hidden="true"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted pointer-events-none"
            >
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
            </span>
            <input
                type="text"
                inputMode="search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-label="전략 검색 (ID·이름·코인·패밀리)"
                placeholder="전략 검색 — h318, 청산, BTC, 베이시스…"
                className="w-full bg-surface-card border border-surface-border text-content text-sm rounded-lg pl-9 pr-9 py-2.5 min-h-11 placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-brand"
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    aria-label="검색어 지우기"
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-7 w-7 rounded text-content-muted hover:text-content hover:bg-surface-hover transition-colors"
                >
                    <span aria-hidden="true">✕</span>
                </button>
            )}
        </div>
    );
}

/**
 * StrategyControls — 정렬 + 보기 모드(카드/리스트) 토글. 상태 필터는
 * StatusRibbon(분포 바 클릭)이 단일 소스로 담당해 중복 컨트롤을 제거했다.
 */
function StrategyControls({
    sortKey,
    onSortChange,
    viewMode,
    onViewModeChange,
    groupMode,
    onGroupModeChange,
    uptime,
    onUptimeChange,
}) {
    const modes = [
        { id: "card", glyph: "⊞", label: "카드" },
        { id: "list", glyph: "☰", label: "리스트" },
    ];
    // 카드 그룹 표시 — 개별(기본)/묶음. 같은 접두어라도 별개 전략(h1320/h1320b/h1320v2)은
    // 개별로 봐야 손익을 정확히 판단할 수 있어 개별이 기본값.
    const groupOpts = [
        { id: "individual", label: "개별", title: "전략을 하나씩 개별 카드로 표시(패밀리 묶음 해제)" },
        { id: "family", label: "묶음", title: "같은 패밀리(코인/파라미터 변형)를 한 카드로 합산 표시" },
    ];
    // 운영기간(holdout 14일) 필터 — 검증 성숙도로 거른다.
    const uptimeOpts = [
        { id: "all", label: "전체", title: "운영기간 무관 전체" },
        { id: "mature", label: `${HOLDOUT_DAYS}일+`, title: `운영기간 ${HOLDOUT_DAYS}일 이상 — holdout 게이트 충족(실전 검증 성숙)` },
        { id: "young", label: `${HOLDOUT_DAYS}일 미만`, title: `운영기간 ${HOLDOUT_DAYS}일 미만 — holdout 미충족(검증 진행 중)` },
    ];
    return (
        <div className="flex flex-wrap items-center gap-2 mb-3">
            <label htmlFor="strategy-sort" className="text-xs text-content-muted">
                정렬
            </label>
            <select
                id="strategy-sort"
                value={sortKey}
                onChange={(e) => onSortChange(e.target.value)}
                className="bg-surface-card border border-surface-border text-content-secondary text-xs px-2 py-1.5 rounded min-h-11 focus:outline-none focus:ring-2 focus:ring-brand"
                aria-label="전략 정렬 기준"
            >
                <option value="category">분류(패밀리)</option>
                <option value="verdict">판정순 (GO 먼저)</option>
                <option value="pnl-asc">PnL 낮은순</option>
                <option value="pnl-desc">PnL 높은순</option>
                <option value="trades-desc">거래 많은순</option>
                <option value="recent">최근 활동순</option>
                <option value="added-desc">최근 추가순</option>
            </select>
            <span className="text-xs text-content-muted ml-1">운영기간</span>
            <div
                className="inline-flex items-center gap-1 bg-surface-card border border-surface-border rounded p-0.5"
                role="group"
                aria-label="운영기간 필터 (holdout 14일)"
            >
                {uptimeOpts.map((o) => {
                    const sel = uptime === o.id;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => onUptimeChange(o.id)}
                            aria-pressed={sel}
                            title={o.title}
                            className={`inline-flex items-center text-xs px-2.5 py-1.5 rounded min-h-11 whitespace-nowrap transition-colors ${
                                sel
                                    ? "bg-brand-subtle text-brand-dark font-medium"
                                    : "text-content-muted hover:text-content hover:bg-surface-hover"
                            }`}
                        >
                            {o.label}
                        </button>
                    );
                })}
            </div>
            <span className="text-xs text-content-muted ml-1">그룹</span>
            <div
                className="inline-flex items-center gap-1 bg-surface-card border border-surface-border rounded p-0.5"
                role="group"
                aria-label="전략 그룹 표시"
            >
                {groupOpts.map((o) => {
                    const sel = groupMode === o.id;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => onGroupModeChange(o.id)}
                            aria-pressed={sel}
                            title={o.title}
                            className={`inline-flex items-center text-xs px-2.5 py-1.5 rounded min-h-11 whitespace-nowrap transition-colors ${
                                sel
                                    ? "bg-brand-subtle text-brand-dark font-medium"
                                    : "text-content-muted hover:text-content hover:bg-surface-hover"
                            }`}
                        >
                            {o.label}
                        </button>
                    );
                })}
            </div>
            <div
                className="ml-auto inline-flex items-center gap-1 bg-surface-card border border-surface-border rounded p-0.5"
                role="group"
                aria-label="전략 보기 모드"
            >
                {modes.map((m) => {
                    const sel = viewMode === m.id;
                    return (
                        <button
                            key={m.id}
                            type="button"
                            onClick={() => onViewModeChange(m.id)}
                            aria-pressed={sel}
                            aria-label={`${m.label} 보기`}
                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded min-h-11 transition-colors ${
                                sel
                                    ? "bg-brand-subtle text-brand-dark font-medium"
                                    : "text-content-muted hover:text-content hover:bg-surface-hover"
                            }`}
                        >
                            <span aria-hidden="true">{m.glyph}</span>
                            {m.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function KpiCard({ label, value, tone = "neutral" }) {
    const toneClass =
        tone === "gain"
            ? "text-gain"
            : tone === "loss"
              ? "text-loss"
              : "text-content";
    return (
        <div className="bg-surface-card border border-surface-border rounded-lg p-3">
            <div className="text-xs text-content-muted">{label}</div>
            <div className={`text-base sm:text-lg font-semibold mt-1 ${toneClass}`}>
                {value}
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────────
 * 성과 차트 sub-view 보강 (DAS-147) — 차트 한 장이 아니라 "성과 분석" 으로.
 *   1) EquityStatsRow   — 누적 수익률·MDD·진입중 평가·운영기간 4칸 요약
 *   2) PnlBreakdownPanel — 청산완료(실현) vs 진입중(평가) 손익 구성 (피드와 동일 멘탈모델)
 * ────────────────────────────────────────────────────────────────────────── */

/** equitySeries(index 100 기준)에서 누적수익률·MDD·현재낙폭·기간 통계 계산. */
function computeEquityStats(series) {
    if (!Array.isArray(series) || series.length === 0) return null;
    let peak = series[0].equity;
    let maxDd = 0;
    for (const p of series) {
        if (p.equity > peak) peak = p.equity;
        const dd = peak > 0 ? ((p.equity - peak) / peak) * 100 : 0;
        if (dd < maxDd) maxDd = dd;
    }
    const current = series[series.length - 1].equity;
    const currentDd = peak > 0 ? ((current - peak) / peak) * 100 : 0;
    const firstMs = Date.parse(series[0].ts);
    const lastMs = Date.parse(series[series.length - 1].ts);
    const spanDays =
        Number.isFinite(firstMs) && Number.isFinite(lastMs)
            ? Math.max(0, Math.round((lastMs - firstMs) / 86_400_000))
            : null;
    return {
        current,
        cumReturnPct: current - 100,
        peak,
        maxDrawdownPct: maxDd,
        currentDrawdownPct: currentDd,
        spanDays,
        count: series.length,
    };
}

const pnlTone = (v) => (v > 0 ? "gain" : v < 0 ? "loss" : "neutral");
const toneText = (t) =>
    t === "gain" ? "text-gain" : t === "loss" ? "text-loss" : "text-content";

function fmtSignedKrwPage(v) {
    const n = Math.round(v ?? 0);
    const s = Math.abs(n).toLocaleString();
    return n > 0 ? `+${s}` : n < 0 ? `-${s}` : s;
}

function fmtPct(v) {
    if (!Number.isFinite(v)) return "—";
    return `${v > 0 ? "+" : ""}${v.toFixed(2)}%`;
}

/** 성과 요약 스탯 1칸. tone 으로 손익 색, pulse 로 진입중(brand) 강조. */
function StatCard({ label, value, sub, tone = "neutral", brand = false, pulse = false }) {
    return (
        <div
            className={`bg-surface-card border rounded-lg p-3 ${
                brand ? "border-brand/40" : "border-surface-border"
            }`}
        >
            <div className="flex items-center gap-1.5">
                {pulse && (
                    <span
                        aria-hidden="true"
                        className="inline-block h-1.5 w-1.5 rounded-full bg-brand animate-pulse"
                    />
                )}
                <span className="text-xs text-content-muted">{label}</span>
            </div>
            <div
                className={`text-base sm:text-lg font-bold tabular-nums mt-1 ${
                    brand ? "text-brand-dark" : toneText(tone)
                }`}
            >
                {value}
            </div>
            {sub != null && (
                <div className="text-xs text-content-muted tabular-nums mt-0.5">{sub}</div>
            )}
        </div>
    );
}

function EquityStatsRow({ stats, unrealizedKrw, openCount, rangeLabel }) {
    if (!stats) return null;
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
                label={`누적 수익률 · ${rangeLabel}`}
                value={fmtPct(stats.cumReturnPct)}
                tone={pnlTone(stats.cumReturnPct)}
                sub={`지수 ${stats.current.toFixed(1)} (시작 100)`}
            />
            <StatCard
                label="최대 낙폭 (MDD)"
                value={fmtPct(stats.maxDrawdownPct)}
                tone={stats.maxDrawdownPct < 0 ? "loss" : "neutral"}
                sub={`현재 낙폭 ${fmtPct(stats.currentDrawdownPct)}`}
            />
            <StatCard
                label="진입중 평가"
                value={`${fmtSignedKrwPage(unrealizedKrw)} KRW`}
                brand
                pulse={openCount > 0}
                sub={openCount > 0 ? `${openCount}건 미실현 보유` : "보유 포지션 없음"}
            />
            <StatCard
                label="운영 기간"
                value={stats.spanDays != null ? `${stats.spanDays}일` : "—"}
                sub={`스냅샷 ${stats.count.toLocaleString()}개`}
            />
        </div>
    );
}

/**
 * PnlBreakdownPanel — 총 손익을 "청산 완료(실현)" 와 "진입중(평가)" 로 분해.
 * 체결 피드의 2-세계(실현/평가)를 성과 탭에서도 한 번 더 보여줘 멘탈 모델을 강화한다.
 * 비율 바: 실현(손익 부호색) | 평가(brand=진입중) — 크기는 |금액| 비례.
 */
function PnlBreakdownPanel({ realizedKrw, unrealizedKrw, openCount, trades, winRate }) {
    const total = (realizedKrw ?? 0) + (unrealizedKrw ?? 0);
    const aR = Math.abs(realizedKrw ?? 0);
    const aU = Math.abs(unrealizedKrw ?? 0);
    const sum = aR + aU;
    const rFrac = sum > 0 ? (aR / sum) * 100 : 100;
    const uFrac = sum > 0 ? (aU / sum) * 100 : 0;
    return (
        <section className="bg-surface-card border border-surface-border rounded-lg p-4">
            <div className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
                <h3 className="text-sm font-semibold text-content">손익 구성</h3>
                <span className="text-xs text-content-muted">
                    청산 완료(실현) + 진입중(평가)
                </span>
            </div>

            {/* 비율 바 */}
            <div
                className="h-2.5 rounded-full overflow-hidden bg-surface-elevated flex"
                role="img"
                aria-label={`실현 ${Math.round(rFrac)}%, 평가 ${Math.round(uFrac)}%`}
            >
                {rFrac > 0 && (
                    <div
                        style={{ width: `${rFrac}%` }}
                        className={realizedKrw >= 0 ? "bg-gain" : "bg-loss"}
                    />
                )}
                {uFrac > 0 && (
                    <div style={{ width: `${uFrac}%` }} className="bg-brand" />
                )}
            </div>

            {/* 3분할 수치 */}
            <div className="grid grid-cols-3 gap-2 mt-3">
                <BreakdownCol
                    dot={realizedKrw >= 0 ? "bg-gain" : "bg-loss"}
                    label="청산 완료 · 실현"
                    value={`${fmtSignedKrwPage(realizedKrw)}`}
                    tone={pnlTone(realizedKrw)}
                    sub={`승률 ${((winRate ?? 0) * 100).toFixed(0)}% · ${(
                        trades ?? 0
                    ).toLocaleString()}거래`}
                />
                <BreakdownCol
                    dot="bg-brand"
                    pulse={openCount > 0}
                    label="진입중 · 평가"
                    value={`${fmtSignedKrwPage(unrealizedKrw)}`}
                    brand
                    sub={openCount > 0 ? `${openCount}건 보유` : "보유 없음"}
                />
                <BreakdownCol
                    label="총 손익 · 평가 반영"
                    value={`${fmtSignedKrwPage(total)}`}
                    tone={pnlTone(total)}
                    sub="실현 + 평가"
                    emphasize
                />
            </div>
        </section>
    );
}

function BreakdownCol({ dot, pulse = false, label, value, sub, tone = "neutral", brand = false, emphasize = false }) {
    return (
        <div className={emphasize ? "pl-2 border-l border-surface-border-light" : ""}>
            <div className="flex items-center gap-1.5 min-w-0">
                {dot && (
                    <span
                        aria-hidden="true"
                        className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${dot} ${
                            pulse ? "animate-pulse" : ""
                        }`}
                    />
                )}
                <span className="text-xs text-content-muted truncate">{label}</span>
            </div>
            <div
                className={`font-bold tabular-nums mt-1 ${
                    emphasize ? "text-base sm:text-lg" : "text-sm sm:text-base"
                } ${brand ? "text-brand-dark" : toneText(tone)}`}
            >
                {value}
                <span className="text-xs font-medium text-content-muted ml-1">KRW</span>
            </div>
            {sub != null && (
                <div className="text-xs text-content-muted mt-0.5 truncate">{sub}</div>
            )}
        </div>
    );
}

function EquityRangeToggle({ value, onChange }) {
    const opts = [
        { id: "7d", label: "7d" },
        { id: "30d", label: "30d" },
        { id: "all", label: "ALL" },
    ];
    return (
        <div className="flex items-center gap-1" role="group" aria-label="차트 기간 선택">
            {opts.map((o) => {
                const sel = value === o.id;
                return (
                    <button
                        key={o.id}
                        type="button"
                        onClick={() => onChange(o.id)}
                        aria-pressed={sel}
                        className={`text-xs px-2 py-1 rounded font-medium transition-colors min-h-11 ${
                            sel
                                ? "bg-brand-subtle text-brand-dark"
                                : "text-content-muted hover:text-content"
                        }`}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * 선택된 range 에 맞게 equity 스냅샷을 잘라낸다. 'all' 은 패스스루.
 * 이후 buildEquitySeries 가 첫 데이터 기준으로 index 100 리베이스하므로
 * 자른 범위의 시작점이 차트의 0% 위치가 된다.
 */
function filterEquityByRange(equity, range) {
    if (
        range === "all" ||
        !Array.isArray(equity) ||
        equity.length === 0
    ) {
        return equity;
    }
    const days = range === "7d" ? 7 : range === "30d" ? 30 : null;
    if (!days) return equity;
    const since = Date.now() - days * 24 * 3600 * 1000;
    return equity.filter((p) => {
        const t = new Date(p.ts ?? p.Ts).getTime();
        return Number.isFinite(t) && t >= since;
    });
}

function buildEquitySeries(equity) {
    if (!Array.isArray(equity) || equity.length === 0) return [];
    const first = equity[0].equity_krw ?? equity[0].EquityKRW ?? 1;
    if (!first) return [];
    return equity.map((p) => {
        const v = p.equity_krw ?? p.EquityKRW ?? 0;
        return {
            ts: p.ts ?? p.Ts,
            equity: (v / first) * 100,
            drawdown: 0,
        };
    });
}

function fmtUptime(sec) {
    if (sec == null) return "—";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${m}m`;
}

export default function PaperOverviewPage() {
    const navigate = useNavigate();
    return <PaperOverviewPageInner navigate={navigate} />;
}
